import React, {Component} from "react"
import {Route} from "react-router-dom"
import PropTypes from "prop-types"
import {LangLoader} from "../../lang"
import {ContextRouteProvider} from "./ContextRouteProvider"

interface ContextRouteProps {
  app: string
  apps: {[key: string]: string}
  routes: any
  routesProps: any
}

/**
 * Top-level route that provides *application context* for all child routes.
 *
 * It maps an `app` key (e.g., "wallet", "home") to its base path using the
 * provided `apps` dictionary.  Everything under that base path is rendered
 * through the given `routes` component, with translations (`LangLoader`) and
 * context (`ContextRouteProvider`) automatically wired in.
 *
 * Usage example:
 * ```tsx
 * <ContextRoute
 *   app="wallet"
 *   apps={{wallet: "/wallet", home: "/"}}
 *   routes={WalletRoutes}
 * />
 * ```
 *
 * The `routes` render function will receive a `path` prop corresponding to the
 * resolved base path for the selected `app`.
 */
export default class ContextRoute extends Component<ContextRouteProps> {
  static defaultProps = {
    routesProps: {}
  }

  static childContextTypes = {
    appPath: PropTypes.string.isRequired,
    apps: PropTypes.object.isRequired,
    app: PropTypes.string.isRequired
  }

  getChildContext() {
    return {
      appPath: this.getPath(),
      app: this.props.app,
      apps: this.props.apps
    }
  }

  getPath = () => this.props.apps[this.props.app]

  render() {
    const {routes: Routes, routesProps, app} = this.props
    const path = this.getPath()

    return (
      <ContextRouteProvider app={this.props.app} apps={this.props.apps}>
        <Route
          path={path}
          render={() => (
            <LangLoader bucket={app}>
              <Routes path={path} {...routesProps} />
            </LangLoader>
          )}
        />
      </ContextRouteProvider>
    )
  }
}
